import { IInvitationDto, IRoleDto, IUserDto, IUserInfoDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import * as React from "react";
import { RoleService } from "../../../../services/role-service";
import { UserService } from "../../../../services/user-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./user-page.scss";

export interface IUserPageState {
  userRoles: string[];
  allRoles: IRoleDto[];
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  emailsErrorMessage: string | undefined;
}

/** What each of the dialog's two commits is doing, rather than the generic "Saving". */
const UPDATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Updating", success: "Updated" };
const INVITE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Sending", success: "Sent" };

/** The `id` the footer's submit button targets — see `KModalFooter.submitForm`. */
const FORM_ID: string = "user-invite-form";

export interface IUserPageProps {
  /** An existing member when editing, or an empty shape when inviting. */
  user: IUserDto | IUserInfoDto;
  isUserModalOpen: boolean;
  /** `revealUserId` is the member the list should walk to and light up, when one was edited. */
  closeModal: (revealUserId?: string) => void;
  users: IUserDto[];
}

declare let window: Window & { [key: string]: any };

class UserPage extends React.PureComponent<IUserPageProps & IWithContextProp & IWithHistoryProp, IUserPageState> {
  formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IUserPageProps & IWithContextProp & IWithHistoryProp) {
    super(props);

    this.state = {
      userRoles: [],
      allRoles: [],
      saveStatus: "idle",
      emailsErrorMessage: undefined
    };
    this.loadAllRoles();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  async loadAllRoles() {
    try {
      const roles = await RoleService.GetAll();
      if (roles.status === 200) {
        this.setState({
          allRoles: roles.data
        });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  // mail sunucusu (SES) ASCII dışı karakteri reddediyor, istemcide durdurmak gerekiyor
  hasOnlyEnglishCharacters(email: string): boolean {
    // boşluk/karakter formatı ayrıca validateEmail'de yakalanıyor, burada sadece ASCII dışını arıyoruz
    return !/[^\x20-\x7e]/.test(email);
  }

  validateEmail(email: string): boolean {
    const re: RegExp =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return this.hasOnlyEnglishCharacters(email) && re.test(email);
  }

  isRegisteredEmail(email: string): boolean {
    return this.props.users.some((u) => u.username === email);
  }

  getEmailErrorMessage(email: string): string | undefined {
    if (!this.hasOnlyEnglishCharacters(email)) {
      return "An email address cannot contain non-English characters such as ı, ş, ğ, ü, ö or ç.";
    }
    if (!this.validateEmail(email)) {
      return "This is not a valid email address.";
    }
    if (this.isRegisteredEmail(email)) {
      return "This email address is already registered.";
    }
    return undefined;
  }

  // hem etiketler değiştikçe alan altında hem de Send Invite'ta aynı mesaj gösteriliyor
  getEmailsErrorMessage(emails: string[]): string | undefined {
    const nonEnglishEmails: string[] = emails.filter((u: string): boolean => !this.hasOnlyEnglishCharacters(u));
    if (nonEnglishEmails.length > 0) {
      return `The following email addresses contain non-English characters (such as ı, ş, ğ, ü, ö, ç) and cannot be invited: [${nonEnglishEmails.join(", ")}]. Please enter them with English characters only.`;
    }

    const malformedEmails: string[] = emails.filter((u: string): boolean => !this.validateEmail(u));
    if (malformedEmails.length > 0) {
      return `The following email addresses are not valid: [${malformedEmails.join(", ")}]`;
    }

    const registeredEmails: string[] = emails.filter((u: string): boolean => this.isRegisteredEmail(u));
    if (registeredEmails.length > 0) {
      return `The following email addresses are already registered: [${registeredEmails.join(", ")}]`;
    }

    return undefined;
  }

  handleEmailsChange = (emails: string[]): void => {
    this.setState({ emailsErrorMessage: this.getEmailsErrorMessage(emails ?? []) });
  };

  // etiket oluşmasını beklemeden, yazılan ilk ASCII dışı karakterde uyarıyoruz
  handleEmailSearch = (searchText: string): void => {
    const hasNonEnglishCharacter: boolean = searchText.length > 0 && !this.hasOnlyEnglishCharacters(searchText);
    if (hasNonEnglishCharacter) {
      this.setState({
        emailsErrorMessage:
          "An email address cannot contain non-English characters such as ı, ş, ğ, ü, ö or ç. Please enter it with English characters only."
      });
      return;
    }

    const emails: string[] = this.formRef.current?.getFieldValue("userName") ?? [];
    this.setState({ emailsErrorMessage: this.getEmailsErrorMessage(emails) });
  };

  saveUser = async (values) => {
    // A second submit while the commit runs or its ✓ is held (the button is live again during the
    // hold, and Enter submits the form regardless) would call the service twice and cancel the close.
    if (this.saveStatus.current === "loading" || this.saveStatus.current === "success") return;
    this.saveStatus.start();
    const roleArr: string[] = [];
    if (this.isOnlyRoleFullAccess()) {
      const role: string = this.state.allRoles[0].id;
      roleArr.push(role);
    }
    try {
      if (this.props.user.id) {
        const obj = {} as IUserDto;
        obj.username = values.userName;
        obj.firstName = values.firstName;
        obj.lastName = values.lastName;
        obj.roleIds = this.isOnlyRoleFullAccess() ? roleArr : values.roleids;
        obj.id = this.props.user.id;

        await UserService.Update(this.props.user.id, obj);
        // No toast: the ✓ says it saved and the list then walks to the row it changed, which says
        // what changed better than a line of text could — see `SaveStatus`.
        this.saveStatus.succeed((): void => this.props.closeModal(this.props.user.id));
      } else {
        const emails: string[] = values.userName as string[];
        const emailsErrorMessage: string | undefined = this.getEmailsErrorMessage(emails);
        if (emailsErrorMessage) {
          this.setState({ emailsErrorMessage });
          KMainFunctions.displayWarningNotification(emailsErrorMessage);
          this.saveStatus.reset();
          return;
        }

        const obj = {} as IInvitationDto;
        obj.usernames = emails;

        await UserService.Invite(obj);
        // An invitation lands in someone else's inbox, so the toast is the only place the result
        // can be said; the ✓ still goes up before the dialog leaves.
        KMainFunctions.displaySuccessNotification(`[${obj.usernames}] successfully invited.`);
        // Not `handleClickCancel`: the callback runs while the machine is still on "success", which
        // that handler swallows — the dialog would stay open and the list never reload.
        this.saveStatus.succeed(this.closeDialog);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.saveStatus.fail();
    }
  };

  handleClickCancel = () => {
    // Closing during the ✓ hold would cancel the close the success already scheduled (and with it
    // the reload/refresh that follows), so it is swallowed; the dialog leaves on its own a moment later.
    if (this.saveStatus.current === "success") return;
    // The dialog stays mounted between openings, so a commit abandoned mid-flight must not leave
    // the submit guard latched for the next one.
    this.saveStatus.reset();
    this.closeDialog();
  };

  private closeDialog = (): void => {
    this.setState({ emailsErrorMessage: undefined });
    this.props.closeModal();
  };

  isOnlyRoleFullAccess = () => {
    if (this.state.allRoles.length === 1 && this.state.allRoles.find((x) => x.name === "Full Access")) {
      return true;
    }
    return false;
  };

  handleValidateAndPushEmailsOnCopyToClipBoard = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const values = text.split(/\r?\n/).map((value) => value.trim());
        const userName: string[] = [...(this.formRef.current?.getFieldValue("userName") ?? [])];
        // const invalidUsernameIndexes: number[] = [];
        for (let i = 0; i < values.length; i++) {
          const value = values[i];
          if (!value) continue;
          if (userName.includes(value)) continue;
          // if (!this.validateEmail(value)) {
          //   invalidUsernameIndexes.push(userName.length);
          // }
          userName.push(value);
        }

        this.formRef.current?.setFieldsValue({
          ...this.formRef.current.getFieldsValue(),
          userName
        });
        // setFieldsValue Select'in onChange'ini tetiklemiyor, uyarıyı elle güncelliyoruz
        this.handleEmailsChange(userName);
      })
      .catch((err) => {
        KMainFunctions.exceptionHandler(err);
      });
  };

  handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.code === "KeyV") {
      e.preventDefault();
      e.stopPropagation();
      this.handleValidateAndPushEmailsOnCopyToClipBoard();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 13 && !e.target.value) {
      e.preventDefault();
      e.stopPropagation();
      this.formRef.current?.submit();
      return;
    }

    if (e.keyCode === 188) {
      e.preventDefault();
      e.stopPropagation();
      e.target.blur();
      e.target.focus();
      return;
    }
  };

  private getModalTitle = (user: IUserInfoDto) => {
    if (window.userInfo?.id === user.id) {
      return "Edit profile";
    }

    if (!user.id) {
      return "Invite team member";
    }

    return "Edit invited team member";
  };

  /** One email chip. Red when the address is rejected, with the reason on its title. */
  private renderEmailTag = ({ value, label, onClose, closable }: CustomTagProps): React.ReactElement => {
    const errorMessage: string | undefined = this.getEmailErrorMessage(String(value ?? ""));
    return (
      <span className="kuika-user-invite__tag" title={errorMessage}>
        <KuikaBadge
          content={label}
          color={errorMessage ? "red" : "grey"}
          variant="subtle"
          size="medium"
          rightIcon={
            closable ? (
              // A real button, so the chip's remove action is reachable from the keyboard too. The
              // mouse-down is still held back so a click does not pull focus out of the address box.
              <button
                type="button"
                className="kuika-user-invite__tag-close"
                aria-label={`Remove ${String(value ?? "")}`}
                onMouseDown={(e: React.MouseEvent): void => e.preventDefault()}
                onClick={onClose}
              >
                <KuikaSvg type="common" name="x" width={10} height={10} />
              </button>
            ) : undefined
          }
        />
      </span>
    );
  };

  /** Editing an existing member: the username is fixed, the names are editable. */
  private renderMemberFields(isMainAI: boolean): React.ReactNode {
    return (
      <>
        <div className="kuika-user-invite__form-item">
          <KuikaSmallText className="kuika-user-invite__label">Username</KuikaSmallText>
          <Form.Item name="userName" rules={[{ required: true, message: "Please input a user name!" }]}>
            <KuikaInput
              data-testid="userNameInput"
              placeholder={!isMainAI ? "kuika@kuika.com" : "Enter email address"}
              size="md"
              disabled
            />
          </Form.Item>
        </div>

        <div className="kuika-user-invite__form-item">
          <KuikaSmallText className="kuika-user-invite__label">First Name</KuikaSmallText>
          <Form.Item name="firstName" rules={[{ required: false, message: "Please input a first name!" }]}>
            <KuikaInput data-testid="userFirstNameInput" size="md" autoFocus />
          </Form.Item>
        </div>

        <div className="kuika-user-invite__form-item">
          <KuikaSmallText className="kuika-user-invite__label">Last Name</KuikaSmallText>
          <Form.Item name="lastName" rules={[{ required: false, message: "Please input a last name!" }]}>
            <KuikaInput data-testid="userLastNameInput" size="md" />
          </Form.Item>
        </div>
      </>
    );
  }

  /**
   * Inviting: one tags box collecting addresses. The dropdown stays shut — there is nothing
   * to pick from, the box exists to turn typing into chips.
   */
  private renderInviteFields(isMainAI: boolean): React.ReactNode {
    return (
      <div className="kuika-user-invite__form-item">
        <Form.Item name="userName" rules={[{ required: true, message: "Please input a user name!" }]} noStyle>
          <KuikaSelectbox
            data-testid="userEmailInput"
            data-onboarding-id="settings_users_email-input"
            className="kuika-user-invite__emails"
            placeholder={!isMainAI ? "kuika@kuika.com" : "Enter email address"}
            mode="tags"
            visible={false}
            tokenSeparators={[","]}
            handleKeyDown={this.handleKeyDown}
            onChange={this.handleEmailsChange}
            onSearch={this.handleEmailSearch}
            tagRender={this.renderEmailTag}
            size="md"
          />
        </Form.Item>
        <KuikaSmallText
          data-testid="userEmailHint"
          className={
            this.state.emailsErrorMessage
              ? "kuika-user-invite__hint kuika-user-invite__hint--error"
              : "kuika-user-invite__hint"
          }
        >
          {this.state.emailsErrorMessage ?? "Separate with a comma or press enter."}
        </KuikaSmallText>
      </div>
    );
  }

  render() {
    const isMainAI: boolean = KMainFunctions.isMainAI();
    const isEditing: boolean = this.props.user.id !== undefined;

    return (
      <KModal
        showModal={this.props.isUserModalOpen}
        closeModal={this.handleClickCancel}
        header={this.getModalTitle(this.props.user as IUserInfoDto)}
        closeTestId="closeInviteModalButton"
        footer={
          <KModalFooter
            submitLabel={isEditing ? "Update" : "Send Invite"}
            submitTestId="sendInviteButton"
            submitOnboardingId="settings_users_send-invite"
            submitForm={FORM_ID}
            status={this.state.saveStatus}
            statusLabels={isEditing ? UPDATE_STATUS_LABELS : INVITE_STATUS_LABELS}
          />
        }
        width={480}
        centered
        isLoading={this.state.saveStatus === "loading"}
      >
        <Form
          id={FORM_ID}
          name={FORM_ID}
          ref={this.formRef}
          initialValues={{
            remember: true,
            userName: this.props.user?.username,
            firstName: this.props.user?.firstName,
            lastName: this.props.user?.lastName,
            roles: this.props.user?.roles
          }}
          onFinish={this.saveUser}
        >
          <div className="kuika-user-invite">
            {isEditing ? this.renderMemberFields(isMainAI) : this.renderInviteFields(isMainAI)}
          </div>
        </Form>
      </KModal>
    );
  }
}

const userPage = withContext<IUserPageProps>(
  withHistory<IUserPageProps & IWithContextProp & IWithHistoryProp>(UserPage)
);
export { userPage as UserPage };
